<p-confirmDialog
  key="cardDeleteDialog"
  styleClass="card-delete-confirm-dialog"
  [style]="{ width: 'min(420px, 92vw)' }"
/>

<div class="container-fluid cards-page">
  <app-work-context-bar />

  <div class="cards-shell">
    <header class="cards-hero">
      <div class="cards-hero-copy">
        <p class="cards-kicker">Patient &amp; household cards</p>
        <h1 class="cards-title">Find who belongs where</h1>
        <p class="cards-subtitle">
          Search by patient name, phone, or card number — then open the card, move people, or settle dues.
        </p>
      </div>
      <div class="cards-hero-actions">
        <a routerLink="/admin/patient-registration" class="cards-btn cards-btn--ghost">
          <i class="pi pi-user-plus" aria-hidden="true"></i>
          Register patient
        </a>
        <button type="button" class="cards-btn cards-btn--primary" (click)="openCreateCard()">
          <i class="pi pi-id-card" aria-hidden="true"></i>
          New card
        </button>
      </div>
    </header>

    <section class="cards-search" aria-label="Search patients and cards">
      <div class="cards-search-field">
        <i class="pi pi-search cards-search-icon" aria-hidden="true"></i>
        <input
          type="text"
          class="cards-search-input"
          placeholder="Search patient name, phone, or card number…"
          [ngModel]="searchTerm"
          (ngModelChange)="onSearchInput($event)"
          autocomplete="off"
          spellcheck="false"
        />
        <button
          type="button"
          class="cards-search-clear"
          *ngIf="searchTerm"
          (click)="clearSearch()"
          aria-label="Clear search"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
        <span class="cards-search-spinner" *ngIf="isSearching" aria-live="polite">
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
        </span>
      </div>
      <div class="cards-search-meta" *ngIf="hasSearched && !isSearching">
        <span>{{ matchedPatientCount }} patient{{ matchedPatientCount === 1 ? '' : 's' }}</span>
        <span class="dot" aria-hidden="true">·</span>
        <span>{{ resultCardCount }} card{{ resultCardCount === 1 ? '' : 's' }}</span>
      </div>
    </section>

    <div class="cards-workspace" *ngIf="hasSearched; else idleState">
    <section class="cards-results">
      <article
        class="result-row"
        *ngFor="let row of patientRows; trackBy: trackByRowKey"
        [class.is-matched]="row.matched"
        [class.is-empty-card]="!row.patient"
        [class.is-selected]="activeCard?.id === row.card.id"
      >
        <ng-container *ngIf="row.patient">
          <div class="result-person">
            <span class="result-avatar" aria-hidden="true">{{ getPatientInitials(row.patient) }}</span>
            <div class="result-person-copy">
              <div class="result-name-row">
                <strong class="result-name">{{ getPatientDisplayName(row.patient) }}</strong>
                <span class="result-match-pill" *ngIf="row.matched">Match</span>
              </div>
              <div class="result-subline">
                <span *ngIf="row.patient.patient_number">
                  <i class="pi pi-hashtag" aria-hidden="true"></i>
                  {{ row.patient.patient_number }}
                </span>
                <span *ngIf="row.patient.contact_number">
                  <i class="pi pi-phone" aria-hidden="true"></i>
                  {{ row.patient.contact_number }}
                </span>
                <span *ngIf="row.patient.age != null">
                  <i class="pi pi-calendar" aria-hidden="true"></i>
                  Age {{ row.patient.age }}
                </span>
              </div>
            </div>
          </div>

          <button type="button" class="result-card-chip" (click)="openCard(row.card, row.patient.id)">
            <span class="result-card-chip-top">
              <i class="pi pi-id-card" aria-hidden="true"></i>
              Card {{ row.card.number }}
            </span>
            <span class="result-card-chip-meta">
              <span [class]="getStatusClass(row.card.status)">{{ formatStatus(row.card.status) }}</span>
              <span>·</span>
              <span>{{ row.card.patient_count || 0 }} member{{ (row.card.patient_count || 0) === 1 ? '' : 's' }}</span>
              <span>·</span>
              <span>₹ {{ formatRupee(row.card.total_dues) }}</span>
            </span>
          </button>

          <div class="result-actions">
            <button type="button" class="cards-btn cards-btn--soft" (click)="openCard(row.card, row.patient.id); $event.stopPropagation()">
              Open card
            </button>
            <button type="button" class="cards-btn cards-btn--move" (click)="openMove(row.patient, row.card); $event.stopPropagation()">
              <i class="pi pi-arrow-right-arrow-left" aria-hidden="true"></i>
              Move
            </button>
          </div>
        </ng-container>

        <ng-container *ngIf="!row.patient">
          <div class="result-person">
            <span class="result-avatar result-avatar--empty" aria-hidden="true">
              <i class="pi pi-inbox"></i>
            </span>
            <div class="result-person-copy">
              <strong class="result-name">Empty card</strong>
              <div class="result-subline">
                <span>No patients linked yet</span>
              </div>
            </div>
          </div>
          <button type="button" class="result-card-chip" (click)="openCard(row.card)">
            <span class="result-card-chip-top">
              <i class="pi pi-id-card" aria-hidden="true"></i>
              Card {{ row.card.number }}
            </span>
            <span class="result-card-chip-meta">
              <span [class]="getStatusClass(row.card.status)">{{ formatStatus(row.card.status) }}</span>
              <span>·</span>
              <span>₹ {{ formatRupee(row.card.total_dues) }}</span>
            </span>
          </button>
          <div class="result-actions">
            <button type="button" class="cards-btn cards-btn--soft" (click)="openCard(row.card)">
              Open card
            </button>
          </div>
        </ng-container>
      </article>

      <div class="cards-empty" *ngIf="!patientRows.length && !isSearching">
        <i class="pi pi-search" aria-hidden="true"></i>
        <h3>No matches</h3>
        <p>Try another name, phone, or card number.</p>
      </div>
    </section>
    </div>

    <ng-template #idleState>
      <section class="cards-idle">
        <div class="idle-card">
          <span class="idle-icon" aria-hidden="true"><i class="pi pi-search"></i></span>
          <h3>Start with a person</h3>
          <p>Type a patient name or phone to see which card they belong to.</p>
        </div>
        <div class="idle-card">
          <span class="idle-icon" aria-hidden="true"><i class="pi pi-id-card"></i></span>
          <h3>Or look up a card</h3>
          <p>Search a card number to see every member on that household card.</p>
        </div>
        <div class="idle-card">
          <span class="idle-icon" aria-hidden="true"><i class="pi pi-arrow-right-arrow-left"></i></span>
          <h3>Move in one step</h3>
          <p>Transfer a patient to an existing card or create a new card for them.</p>
        </div>
      </section>
    </ng-template>
  </div>
</div>

<!-- Card drawer -->
<p-sidebar
  [(visible)]="drawerVisible"
  position="right"
  [modal]="true"
  [dismissible]="true"
  [blockScroll]="true"
  appendTo="body"
  [baseZIndex]="14000"
  [style]="{ width: 'min(420px, 96vw)' }"
  styleClass="card-drawer"
  ariaCloseLabel="Close card details"
  (onHide)="onDrawerHide()"
>
  <ng-template pTemplate="header">
    <div class="drawer-header" *ngIf="activeCard">
      <span class="drawer-kicker">Card</span>
      <strong class="drawer-title">{{ activeCard.number || '…' }}</strong>
    </div>
  </ng-template>

  <div class="drawer-loading" *ngIf="drawerLoading">
    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
    Loading card…
  </div>

  <ng-container *ngIf="!drawerLoading && activeCard">
    <div class="drawer-stats">
      <div class="drawer-stat">
        <span class="label">Members</span>
        <strong>{{ drawerPatients.length }}</strong>
      </div>
      <div class="drawer-stat">
        <span class="label">Total dues</span>
        <strong>₹ {{ formatRupee(activeCard.total_dues) }}</strong>
      </div>
      <div class="drawer-stat">
        <span class="label">Status</span>
        <strong [class]="getStatusClass(activeCard.status)">{{ formatStatus(activeCard.status) }}</strong>
      </div>
    </div>

    <div class="drawer-section-label">People on this card</div>

    <div class="drawer-patient" *ngFor="let p of drawerPatients" [id]="'drawer-patient-' + p.id">
      <div class="drawer-patient-main">
        <span class="result-avatar" aria-hidden="true">{{ getPatientInitials(p) }}</span>
        <div class="drawer-patient-copy">
          <strong>{{ getPatientDisplayName(p) }}</strong>
          <div class="result-subline">
            <span *ngIf="p.patient_number">{{ p.patient_number }}</span>
            <span *ngIf="p.contact_number">{{ p.contact_number }}</span>
          </div>
          <span class="dues-inline">Personal dues · ₹ {{ formatRupee(p.personal_dues) }}</span>
        </div>
      </div>
      <div class="drawer-patient-actions">
        <button type="button" class="chip-btn" (click)="openMove(p, activeCard)">
          <i class="pi pi-arrow-right-arrow-left" aria-hidden="true"></i>
          Move
        </button>
        <button
          type="button"
          class="chip-btn chip-btn--settle"
          [class.is-active]="settlePatient?.id === p.id"
          (click)="toggleSettle(p)"
        >
          <i class="pi pi-wallet" aria-hidden="true"></i>
          Settle
        </button>
      </div>

      <div class="settle-panel" *ngIf="settlePatient?.id === p.id">
        <div class="settle-head">
          <strong>Settle dues</strong>
          <button type="button" class="icon-close" (click)="closeSettle()" aria-label="Close settle">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <label class="field-label">Payment mode</label>
        <p-dropdown
          [options]="paymentModeOptions"
          [(ngModel)]="settlePatient.paymentMode"
          optionLabel="label"
          optionValue="value"
          placeholder="Select mode"
          appendTo="body"
          styleClass="w-100"
        />
        <ng-container *ngIf="settlePatient.paymentMode === 'cash'">
          <label class="field-label">Cash amount</label>
          <input type="number" min="0" class="field-input" [(ngModel)]="settlePatient.cashAmount" placeholder="0" />
          <button type="button" class="cards-btn cards-btn--primary w-100" (click)="settleCashDues()">
            Save cash payment
          </button>
        </ng-container>
        <ng-container *ngIf="settlePatient.paymentMode === 'online'">
          <label class="field-label">Online amount</label>
          <input type="number" min="0" class="field-input" [(ngModel)]="settlePatient.onlineAmount" placeholder="0" />
          <button type="button" class="cards-btn cards-btn--primary w-100" (click)="initiateCardDuesPayment()">
            Pay online
          </button>
        </ng-container>
      </div>
    </div>

    <div class="cards-empty drawer-empty" *ngIf="!drawerPatients.length">
      <i class="pi pi-users" aria-hidden="true"></i>
      <p>No patients on this card yet. Move someone here or register a new patient onto it.</p>
      <a routerLink="/admin/patient-registration" class="cards-btn cards-btn--soft">Register patient</a>
    </div>

    <div class="drawer-footer">
      <button type="button" class="cards-btn cards-btn--danger" (click)="confirmDeleteCard(activeCard)">
        <i class="pi pi-trash" aria-hidden="true"></i>
        Delete card
      </button>
    </div>
  </ng-container>
</p-sidebar>

<!-- Create card -->
<p-dialog
  [(visible)]="createDialogVisible"
  [modal]="true"
  [draggable]="false"
  [closable]="true"
  appendTo="body"
  header="Create new card"
  [style]="{ width: 'min(420px, 94vw)' }"
  styleClass="cards-simple-dialog"
  (onHide)="closeCreateCard()"
>
  <p class="dialog-lead">
    We’ll suggest the next number for the current clinic. You can edit it before creating.
  </p>
  <label class="field-label" for="newCardNumber">Card number</label>
  <input
    id="newCardNumber"
    type="text"
    class="field-input"
    [(ngModel)]="createCardNumber"
    [placeholder]="suggestingCardNumber ? 'Suggesting…' : 'e.g. M123'"
    spellcheck="false"
    (keyup.enter)="submitCreateCard()"
  />
  <p class="field-hint" *ngIf="suggestingCardNumber">Fetching next number for this clinic…</p>
  <p class="field-hint" *ngIf="!suggestingCardNumber && createCardNumber">
    Suggested from clinic sequence — edit if needed.
  </p>
  <ng-template pTemplate="footer">
    <button type="button" class="cards-btn cards-btn--ghost" (click)="closeCreateCard()">Cancel</button>
    <button
      type="button"
      class="cards-btn cards-btn--primary"
      [disabled]="createBusy"
      (click)="submitCreateCard()"
    >
      {{ createBusy ? 'Creating…' : 'Create card' }}
    </button>
  </ng-template>
</p-dialog>

<!-- Move sheet -->
<p-dialog
  [(visible)]="moveDialogVisible"
  [modal]="true"
  [draggable]="false"
  [closable]="false"
  [showHeader]="false"
  [dismissableMask]="true"
  appendTo="body"
  [baseZIndex]="13000"
  [style]="{ width: 'min(400px, 94vw)' }"
  styleClass="move-sheet-dialog"
  (onHide)="closeMove()"
>
  <div class="move-sheet" *ngIf="movePatient">
    <header class="move-sheet-header">
      <div class="move-sheet-heading">
        <span class="move-sheet-kicker">Move patient</span>
        <h2 class="move-sheet-title">{{ getPatientDisplayName(movePatient) }}</h2>
      </div>
      <button type="button" class="sheet-close-btn" (click)="closeMove()" aria-label="Close">
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </header>

    <div class="move-route" *ngIf="moveFromCard">
      <div class="move-route-node">
        <span class="move-route-label">From</span>
        <strong>{{ moveFromCard.number }}</strong>
      </div>
      <i class="pi pi-arrow-right move-route-arrow" aria-hidden="true"></i>
      <div
        class="move-route-node"
        [class.is-ready]="
          (moveOption === 'existing' && !!moveTargetCard) ||
          (moveOption === 'new' && !!((moveNewCardNumber || '').trim() || suggestedCardNumber))
        "
      >
        <span class="move-route-label">To</span>
        <strong *ngIf="moveOption === 'existing' && moveTargetCard">{{ moveTargetCard.number }}</strong>
        <strong *ngIf="moveOption === 'new' && ((moveNewCardNumber || '').trim() || suggestedCardNumber)">
          {{ ((moveNewCardNumber || '').trim() || suggestedCardNumber).toUpperCase() }}
        </strong>
        <strong
          class="is-muted"
          *ngIf="(moveOption === 'existing' && !moveTargetCard) || (moveOption === 'new' && !(moveNewCardNumber || '').trim() && !suggestedCardNumber)"
        >
          {{ moveOption === 'new' ? (suggestingCardNumber ? 'Suggesting…' : 'Choose destination') : 'Choose destination' }}
        </strong>
      </div>
    </div>

    <div class="move-seg" role="tablist" aria-label="Destination type">
      <button
        type="button"
        class="move-seg-btn"
        [class.is-active]="moveOption === 'existing'"
        (click)="setMoveOption('existing')"
      >
        Existing card
      </button>
      <button
        type="button"
        class="move-seg-btn"
        [class.is-active]="moveOption === 'new'"
        (click)="setMoveOption('new')"
      >
        New card
      </button>
    </div>

    <div class="move-sheet-body" *ngIf="moveOption === 'existing'">
      <div class="move-selected" *ngIf="moveTargetCard as target">
        <div class="move-selected-copy">
          <strong>Card {{ target.number }}</strong>
          <span>{{ getMoveMemberSummary(target) }}</span>
        </div>
        <button type="button" class="move-selected-clear" (click)="clearMoveTarget()">Change</button>
      </div>

      <ng-container *ngIf="!moveTargetCard">
        <label class="field-label" for="moveTargetQuery">Find card</label>
        <div class="move-inline-search">
          <i class="pi pi-search" aria-hidden="true"></i>
          <input
            id="moveTargetQuery"
            type="text"
            class="field-input move-inline-input"
            [ngModel]="moveTargetQuery"
            (ngModelChange)="onMoveTargetQueryChange($event)"
            placeholder="Card number or patient name"
            autocomplete="off"
            spellcheck="false"
          />
          <i class="pi pi-spin pi-spinner" *ngIf="moveTargetSearching" aria-hidden="true"></i>
        </div>

        <div class="move-result-list" *ngIf="moveTargetResults.length">
          <button
            type="button"
            class="move-result-row"
            *ngFor="let card of moveTargetResults"
            (click)="selectMoveTarget(card)"
          >
            <span class="move-result-id">{{ card.number }}</span>
            <span class="move-result-meta">{{ getMoveMemberSummary(card) }}</span>
          </button>
        </div>

        <p class="field-hint" *ngIf="(moveTargetQuery || '').trim() && !moveTargetSearching && !moveTargetResults.length">
          No other cards match that search.
        </p>
        <p class="field-hint" *ngIf="!(moveTargetQuery || '').trim()">
          Type a card number or a patient already on the destination card.
        </p>
      </ng-container>
    </div>

    <div class="move-sheet-body" *ngIf="moveOption === 'new'">
      <label class="field-label" for="moveNewCardNumber">New card number</label>
      <div class="move-suggest-row">
        <input
          id="moveNewCardNumber"
          type="text"
          class="field-input"
          [(ngModel)]="moveNewCardNumber"
          [placeholder]="suggestingCardNumber ? 'Suggesting…' : 'e.g. M124'"
          spellcheck="false"
          (keyup.enter)="confirmMove()"
        />
        <button
          type="button"
          class="cards-btn cards-btn--soft"
          [disabled]="suggestingCardNumber"
          (click)="useSuggestedCardNumber()"
        >
          {{ suggestingCardNumber ? '…' : 'Refresh' }}
        </button>
      </div>
      <p class="field-hint" *ngIf="suggestingCardNumber">
        Fetching next number for this clinic…
      </p>
      <p class="field-hint" *ngIf="!suggestingCardNumber && suggestedCardNumber">
        Suggested: <strong>{{ suggestedCardNumber }}</strong> — same sequence as registration. You can edit it.
      </p>
      <p class="field-hint" *ngIf="!suggestingCardNumber && !(moveNewCardNumber || '').trim()">
        Leave blank to auto-assign the next clinic card number on confirm.
      </p>
    </div>

    <footer class="move-sheet-footer">
      <button type="button" class="cards-btn cards-btn--ghost" (click)="closeMove()">Cancel</button>
      <button
        type="button"
        class="cards-btn cards-btn--confirm"
        [disabled]="!canConfirmMove || moveBusy"
        (click)="confirmMove()"
      >
        {{ moveBusy ? 'Moving…' : 'Move patient' }}
      </button>
    </footer>
  </div>
</p-dialog>
